import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../firebase/AuthContext';
import { useFirebase } from '../firebase/FirebaseContext';
import { collection, addDoc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';

const CreateVoice = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { db, storage } = useFirebase();
    const [currentStep, setCurrentStep] = useState(1);
    const [uploading, setUploading] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        dataSource: 'zip', // 'zip' or 'huggingface'
        zipFile: null,
        huggingfaceLink: '',
        trainingConfig: {
            audioSampleRate: 16000,
            audioDuration: 10,
            numberOfAudioFiles: 100
        }
    });
    const [selectedFileName, setSelectedFileName] = useState(null);
    const [uploadProgress, setUploadProgress] = useState(0);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        setFormData(prev => ({
            ...prev,
            zipFile: file
        }));
        setSelectedFileName(file ? file.name : null);
    };

    const handleConfigChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            trainingConfig: {
                ...prev.trainingConfig,
                [name]: value
            }
        }));
    };

    const nextStep = () => setCurrentStep(prev => prev + 1);
    const prevStep = () => setCurrentStep(prev => prev - 1);

    const handleCancel = () => {
        navigate('/fine-tuning');
    };

    const handleStartTraining = async () => {
        try {
            setUploading(true);
            setUploadProgress(0);

            // First create the voice model document to get its ID
            const voiceModelData = {
                userId: user.uid,
                name: formData.name,
                description: formData.description,
                dataSource: formData.dataSource,
                huggingfaceLink: formData.huggingfaceLink,
                trainingConfig: formData.trainingConfig,
                status: 'training', // pending, training, completed, failed
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp()
            };

            // Add the document to the voice_models collection
            const docRef = await addDoc(collection(db, 'voice_models'), voiceModelData);

            // If it's a ZIP file, upload it to Firebase Storage
            if (formData.dataSource === 'zip' && formData.zipFile) {
                // Create a reference to the file location in Firebase Storage
                const storageRef = ref(storage, `voice_models/${docRef.id}/training_data.zip`);
                
                // Create upload task with progress monitoring
                const uploadTask = uploadBytesResumable(storageRef, formData.zipFile);
                
                // Monitor upload progress
                uploadTask.on('state_changed',
                    (snapshot) => {
                        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                        setUploadProgress(progress);
                    },
                    (error) => {
                        console.error('Error uploading file:', error);
                        throw error;
                    }
                );
                
                // Wait for upload to complete
                await uploadTask;
                
                // Get the download URL
                const downloadURL = await getDownloadURL(storageRef);
                
                // Update the voice model document with the download URL
                await updateDoc(docRef, {
                    zipFileUrl: downloadURL
                });
            }

            // Navigate back to the fine-tuning page
            navigate('/fine-tuning');
        } catch (error) {
            console.error('Error saving voice model:', error);
            // You might want to show an error message to the user here
        } finally {
            setUploading(false);
            setUploadProgress(0);
        }
    };

    const renderStep = () => {
        switch (currentStep) {
            case 1:
                return (
                    <div className="space-y-8">
                        <div className="flex items-center space-x-4">
                            <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-700 font-medium text-lg">1</div>
                            <div>
                                <h4 className="text-xl font-medium text-gray-900">Basic Information</h4>
                                <p className="text-sm text-gray-500 mt-1">Start by providing basic details about your voice model</p>
                            </div>
                        </div>
                        <div className="space-y-8 max-w-2xl">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Model Name</label>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-1 focus:ring-gray-400 focus:border-gray-400 transition-colors duration-200"
                                    placeholder="Enter a name for your voice model"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                                <textarea
                                    name="description"
                                    value={formData.description}
                                    onChange={handleInputChange}
                                    className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-1 focus:ring-gray-400 focus:border-gray-400 transition-colors duration-200"
                                    placeholder="Describe your voice model"
                                    rows={4}
                                />
                            </div>
                        </div>
                    </div>
                );
            case 2:
                return (
                    <div className="space-y-8">
                        <div className="flex items-center space-x-4">
                            <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-700 font-medium text-lg">2</div>
                            <div>
                                <h4 className="text-xl font-medium text-gray-900">Data Source</h4>
                                <p className="text-sm text-gray-500 mt-1">Choose how you want to provide your training data</p>
                            </div>
                        </div>
                        <div className="space-y-8 max-w-2xl">
                            <div className="grid grid-cols-2 gap-6">
                                <label className={`relative flex items-center p-6 rounded-lg border cursor-pointer transition-colors duration-200 ${formData.dataSource === 'zip' ? 'border-gray-400 bg-gray-50' : 'border-gray-200 hover:border-gray-300'}`}>
                                    <input
                                        type="radio"
                                        name="dataSource"
                                        value="zip"
                                        checked={formData.dataSource === 'zip'}
                                        onChange={handleInputChange}
                                        className="sr-only"
                                    />
                                    <div className="flex items-center space-x-4">
                                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${formData.dataSource === 'zip' ? 'border-gray-600' : 'border-gray-300'}`}>
                                            {formData.dataSource === 'zip' && <div className="w-4 h-4 rounded-full bg-gray-600"></div>}
                                        </div>
                                        <span className="font-medium text-gray-700">Upload ZIP File</span>
                                    </div>
                                </label>
                                <label className={`relative flex items-center p-6 rounded-lg border cursor-pointer transition-colors duration-200 ${formData.dataSource === 'huggingface' ? 'border-gray-400 bg-gray-50' : 'border-gray-200 hover:border-gray-300'}`}>
                                    <input
                                        type="radio"
                                        name="dataSource"
                                        value="huggingface"
                                        checked={formData.dataSource === 'huggingface'}
                                        onChange={handleInputChange}
                                        className="sr-only"
                                    />
                                    <div className="flex items-center space-x-4">
                                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${formData.dataSource === 'huggingface' ? 'border-gray-600' : 'border-gray-300'}`}>
                                            {formData.dataSource === 'huggingface' && <div className="w-4 h-4 rounded-full bg-gray-600"></div>}
                                        </div>
                                        <span className="font-medium text-gray-700">HuggingFace Repository</span>
                                    </div>
                                </label>
                            </div>
                            {formData.dataSource === 'zip' ? (
                                <div className="space-y-6">
                                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                                        <h5 className="text-sm font-medium text-gray-700 mb-2">Required Format</h5>
                                        <p className="text-sm text-gray-600">
                                            Your ZIP file should contain pairs of .wav and .txt files with matching names. For example:
                                        </p>
                                        <div className="mt-2 font-mono text-sm text-gray-600 bg-white p-3 rounded border border-gray-200">
                                            sample1.wav<br/>
                                            sample1.txt<br/>
                                            sample2.wav<br/>
                                            sample2.txt
                                        </div>
                                        <p className="mt-2 text-sm text-gray-600">
                                            Each .wav file should contain clear audio of a single voice, and its corresponding .txt file should contain the exact transcription of that audio.
                                        </p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Upload ZIP File</label>
                                        <div className="mt-1 flex justify-center px-6 pt-8 pb-8 border-2 border-gray-200 border-dashed rounded-lg hover:border-gray-300 transition-colors duration-200">
                                            <div className="space-y-2 text-center">
                                                <svg className="mx-auto h-16 w-16 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                                                    <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                                <div className="flex text-sm text-gray-600 justify-center">
                                                    <label className="relative cursor-pointer rounded-md font-medium text-gray-600 hover:text-gray-500">
                                                        <span>Upload a file</span>
                                                        <input type="file" className="sr-only" accept=".zip" onChange={handleFileChange} />
                                                    </label>
                                                    <p className="pl-1">or drag and drop</p>
                                                </div>
                                                <p className="text-xs text-gray-500">ZIP file containing audio-text pairs</p>
                                                {selectedFileName && (
                                                    <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                                                        <p className="text-sm text-green-700 font-medium">File selected:</p>
                                                        <p className="text-sm text-green-600 truncate">{selectedFileName}</p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="space-y-6">
                                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                                        <h5 className="text-sm font-medium text-gray-700 mb-2">Required Format</h5>
                                        <p className="text-sm text-gray-600">
                                            Your HuggingFace dataset should be structured as a table with two columns:
                                        </p>
                                        <div className="mt-2 font-mono text-sm text-gray-600 bg-white p-3 rounded border border-gray-200">
                                            | audio | text |<br/>
                                        </div>
                                        <p className="mt-2 text-sm text-gray-600">
                                            The 'audio' column should contain audio files, and the 'text' column should contain the exact transcription of each audio file.
                                        </p>
                                        <p className="mt-2 text-sm text-red-400">
                                            Please make sure the repository is public.
                                        </p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">HuggingFace Repository URL</label>
                                        <input
                                            type="text"
                                            name="huggingfaceLink"
                                            value={formData.huggingfaceLink}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-1 focus:ring-gray-400 focus:border-gray-400 transition-colors duration-200"
                                            placeholder="https://huggingface.co/username/repo"
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                );
            case 3:
                return (
                    <div className="space-y-8">
                        <div className="flex items-center space-x-4">
                            <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-700 font-medium text-lg">3</div>
                            <div>
                                <h4 className="text-xl font-medium text-gray-900">Data Configuration</h4>
                                <p className="text-sm text-gray-500 mt-1">Configure the training parameters for your model</p>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-3xl">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Audio Sample Rate (Hz)</label>
                                <input
                                    type="number"
                                    name="audioSampleRate"
                                    value={formData.audioSampleRate}
                                    onChange={handleConfigChange}
                                    className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-1 focus:ring-gray-400 focus:border-gray-400 transition-colors duration-200"
                                    min="1"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Estimated Duration (minutes)</label>
                                <input
                                    type="number"
                                    name="audioDuration"
                                    value={formData.audioDuration}
                                    onChange={handleConfigChange}
                                    className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-1 focus:ring-gray-400 focus:border-gray-400 transition-colors duration-200"
                                    min="1"
                                    max="32"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Estimated Number of Audio Files</label>
                                <input
                                    type="number"
                                    name="numberOfAudioFiles"
                                    value={formData.numberOfAudioFiles}
                                    onChange={handleConfigChange}
                                    className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-1 focus:ring-gray-400 focus:border-gray-400 transition-colors duration-200"
                                    min="1"
                                />
                            </div>
                        </div>
                    </div>
                );
            case 4:
                return (
                    <div className="space-y-8">
                        <div className="flex items-center space-x-4">
                            <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-700 font-medium text-lg">4</div>
                            <div>
                                <h4 className="text-xl font-medium text-gray-900">Review and Start Training</h4>
                                <p className="text-sm text-gray-500 mt-1">Review your configuration before starting the training process</p>
                            </div>
                        </div>
                        <div className="space-y-6 max-w-3xl">
                            <div>
                                <h5 className="text-sm font-medium text-gray-500 mb-3">Model Details</h5>
                                <div className="bg-white p-6 rounded-lg space-y-4 border border-gray-200">
                                    <p className="flex justify-between">
                                        <span className="text-gray-600">Name:</span>
                                        <span className="font-medium text-gray-900">{formData.name}</span>
                                    </p>
                                    <p className="flex justify-between">
                                        <span className="text-gray-600">Description:</span>
                                        <span className="font-medium text-gray-900">{formData.description}</span>
                                    </p>
                                    <p className="flex justify-between">
                                        <span className="text-gray-600">Data Source:</span>
                                        <span className="font-medium text-gray-900">{formData.dataSource === 'zip' ? 'ZIP File' : 'HuggingFace Repository'}</span>
                                    </p>
                                    {formData.dataSource === 'huggingface' && (
                                        <p className="flex justify-between">
                                            <span className="text-gray-600">Repository:</span>
                                            <span className="font-medium text-gray-900">{formData.huggingfaceLink}</span>
                                        </p>
                                    )}
                                </div>
                            </div>
                            <div>
                                <h5 className="text-sm font-medium text-gray-500 mb-3">Training Configuration</h5>
                                <div className="bg-white p-6 rounded-lg space-y-4 border border-gray-200">
                                    <p className="flex justify-between">
                                        <span className="text-gray-600">Audio Sample Rate:</span>
                                        <span className="font-medium text-gray-900">{formData.audioSampleRate}</span>
                                    </p>
                                    <p className="flex justify-between">
                                        <span className="text-gray-600">Estimated Duration:</span>
                                        <span className="font-medium text-gray-900">{formData.audioDuration}</span>
                                    </p>
                                    <p className="flex justify-between">
                                        <span className="text-gray-600">Estimated Number of Audio Files:</span>
                                        <span className="font-medium text-gray-900">{formData.numberOfAudioFiles}</span>
                                    </p>
                                </div>
                            </div>
                            {uploading && (
                                <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                                    <div className="flex items-center justify-between mb-2">
                                        <p className="text-sm font-medium text-blue-700">Uploading file...</p>
                                        <p className="text-sm text-blue-600">{Math.round(uploadProgress)}%</p>
                                    </div>
                                    <div className="w-full bg-blue-200 rounded-full h-2">
                                        <div 
                                            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                            style={{ width: `${uploadProgress}%` }}
                                        ></div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="mb-8">
                    <h1 className="text-3xl font-medium text-gray-900 mb-2">Create New Voice Model</h1>
                    <p className="text-gray-500">Train a custom voice model with your audio samples</p>
                </div>

                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                    <div className="p-8">
                        {renderStep()}
                    </div>

                    <div className="border-t border-gray-200 bg-gray-50 px-8 py-6">
                        <div className="flex justify-between items-center">
                            <div className="text-sm text-gray-500">
                                Step {currentStep} of 4
                            </div>
                            <div className="flex space-x-4">
                                <button
                                    onClick={handleCancel}
                                    className="inline-flex justify-center rounded-lg border border-gray-300 shadow-sm px-6 py-3 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none sm:text-sm"
                                >
                                    Cancel
                                </button>
                                {currentStep > 1 && (
                                    <button
                                        onClick={prevStep}
                                        className="inline-flex justify-center rounded-lg border border-gray-300 shadow-sm px-6 py-3 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none sm:text-sm"
                                    >
                                        Previous
                                    </button>
                                )}
                                {currentStep < 4 ? (
                                    <button
                                        onClick={nextStep}
                                        className="inline-flex justify-center rounded-lg border border-transparent shadow-sm px-6 py-3 bg-gray-900 text-base font-medium text-white hover:bg-gray-800 focus:outline-none sm:text-sm"
                                    >
                                        Next
                                    </button>
                                ) : (
                                    <button
                                        onClick={handleStartTraining}
                                        disabled={uploading}
                                        className={`inline-flex justify-center rounded-lg border border-transparent shadow-sm px-6 py-3 bg-gray-900 text-base font-medium text-white hover:bg-gray-800 focus:outline-none sm:text-sm ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    >
                                        {uploading ? 'Uploading...' : 'Start Training'}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CreateVoice; 